import React, { useState, useEffect, useMemo } from 'react';
import { FiGitBranch as GitBranchIcon, FiSearch as SearchIcon } from 'react-icons/fi';
import { lineageAPI, catalogAPI } from '../../../services/dataopsApi';
import LineageGraph, { LineageNode } from '../components/LineageGraph';
import { toFlowGraph } from '../utils/lineageGraphTransform';
import { markDataOpsStepComplete } from '../../../hooks/useDataOpsOnboardingStatus';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';

interface Dataset {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface LineageGraph_ {
  nodes?: unknown[];
  edges?: unknown[];
  focus_node_id?: string | number | null;
  [key: string]: unknown;
}

const LineagePage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<string | null>(null);
  const [hops, setHops] = useState(2);
  const [graph, setGraph] = useState<LineageGraph_>({ nodes: [], edges: [] });
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();

  const selectedDatasetRecord = datasets.find((d) => d.name === selectedDataset) || datasets[0];
  const focusNodeId =
    graph.focus_node_id == null
      ? (selectedDatasetRecord?.id ?? null)
      : graph.focus_node_id;

  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    const result = toFlowGraph(graph as Parameters<typeof toFlowGraph>[0], focusNodeId);
    return {
      nodes: result.nodes as unknown as LineageNode[],
      edges: result.edges as unknown as import('../components/LineageGraph').LineageEdge[],
    };
  }, [graph, focusNodeId]);

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markDataOpsStepComplete('view_lineage');
  }, []);

  useEffect(() => {
    setSelectedDataset(null);
    setDatasets([]);
    setGraph({ nodes: [], edges: [] });
    loadDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, filterType, filterId]);

  useEffect(() => {
    if (selectedDataset && datasets.length > 0) {
      loadLineage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset, hops, datasets.length]);

  const loadDatasets = async () => {
    try {
      const data: Dataset[] = await catalogAPI.getDatasets();
      setDatasets(data);
      if (data.length > 0) {
        setSelectedDataset((prev) => prev || data[0].name);
      }
    } catch (err: unknown) {
      console.error('Error loading datasets:', err instanceof Error ? err.message : err);
    }
  };

  const loadLineage = async () => {
    setLoading(true);
    try {
      const dataset = datasets.find((d) => d.name === selectedDataset) || datasets[0];
      if (dataset) {
        const data = await lineageAPI.getGraph({ dataset_id: dataset.id, hops });
        setGraph(data);
      }
    } catch (err: unknown) {
      console.error('Error loading lineage:', err instanceof Error ? err.message : err);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = (node: LineageNode) => {
    if (node?.data?.label) {
      const match = datasets.find((d) => d.name === node.data.label);
      if (match?.name !== selectedDataset) {
        setSelectedDataset(match!.name);
      }
    }
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <GitBranchIcon className="w-12 h-12 text-blue-600 dark:text-blue-400 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Data Lineage</h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">Visualize data flow and dependencies</p>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedDataset ?? ''}
                onChange={(e) => setSelectedDataset(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Select dataset"
              >
                {datasets.map((dataset) => (
                  <option key={dataset.id} value={dataset.name}>
                    {dataset.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 dark:text-gray-400" htmlFor="lineage-hops">
              Hops:
            </label>
            <select
              id="lineage-hops"
              value={hops}
              onChange={(e) => setHops(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Lineage depth in hops"
            >
              <option value={1}>1 hop</option>
              <option value={2}>2 hops</option>
              <option value={3}>3 hops</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 min-h-[500px]">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading lineage...</div>
          </div>
        ) : flowNodes.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            No lineage data available
          </div>
        ) : (
          <LineageGraph
            nodes={flowNodes}
            edges={flowEdges}
            onNodeClick={handleNodeClick}
          />
        )}
      </div>
    </div>
  );
};

export default LineagePage;
